import React, { FC, useState } from 'react';
import { Box, Button, CircularProgress, useTheme } from '@mui/material';
import { CreditCard } from '@mui/icons-material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { EAccountType } from 'common/accounts.interface';
import DescriptionIcon from '@mui/icons-material/Description';
import FileDownload from '@mui/icons-material/FileDownload';
import { BankingService } from 'services';
import { useSnackbar } from 'notistack';
import { DateTime } from 'luxon';
import ExportModal from '../modals/ExportModal';
import IncomeReportModal from '../modals/IncomeReportModal';

interface AccountTypeSelectorProps {
  startDate: DateTime;
  endDate: DateTime;
  accountType: EAccountType;
  onAccountTypeChange: (type: EAccountType) => void;
}

const bankingService = new BankingService();

export const TransactionsTypeSelector: FC<AccountTypeSelectorProps> = ({ startDate, endDate, accountType, onAccountTypeChange }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [isDownloading, setIsDownloading] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isIncomeReportModalOpen, setIsIncomeReportModalOpen] = useState(false);

  const handleDownloadTransactions = async () => {
    try {
      setIsDownloading(true);
      const blob = await bankingService.downloadTransactionsSheet({
        startDate: startDate.toISODate(),
        endDate: endDate.toISODate(),
        accountType,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `extrato_${startDate.toISODate()}_to_${endDate.toISODate()}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar('Planilha de transações baixada com sucesso!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Erro ao baixar a planilha de transações. Contate o suporte.', { variant: 'error' });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<CreditCard />}
          onClick={() => onAccountTypeChange(EAccountType.PAYMENT)}
          sx={{
            borderRadius: '999px',
            px: 3,
            height: 40,
            textTransform: 'none',
            cursor: accountType === EAccountType.PAYMENT ? 'default' : 'pointer',
            fontWeight: accountType === EAccountType.PAYMENT ? 600 : 500,
            fontSize: '16px',
            color: accountType === EAccountType.PAYMENT ? 'primary.main' : 'text.disabled',
            borderColor: accountType === EAccountType.PAYMENT ? 'primary.main' : 'divider',
            backgroundColor: accountType === EAccountType.PAYMENT ? '#F2F4F8' : 'white',
            '&:hover': {
              backgroundColor: '#F2F4F8',
              borderColor: 'primary.main',
              color: 'primary.main',
            },
          }}
        >
          Conta movimento
        </Button>

        <Button
          variant="outlined"
          startIcon={<AccountBalanceWalletOutlinedIcon />}
          onClick={() => onAccountTypeChange(EAccountType.CONTROL)}
          sx={{
            borderRadius: '999px',
            px: 3,
            height: 40,
            cursor: accountType === EAccountType.CONTROL ? 'default' : 'pointer',
            textTransform: 'none',
            fontWeight: accountType === EAccountType.CONTROL ? 600 : 500,
            fontSize: '16px',
            color: accountType === EAccountType.CONTROL ? 'primary.main' : 'text.disabled',
            borderColor: accountType === EAccountType.CONTROL ? 'primary.main' : 'divider',
            backgroundColor: accountType === EAccountType.CONTROL ? '#F2F4F8' : 'white',
            '&:hover': {
              backgroundColor: '#F2F4F8',
              borderColor: 'primary.main',
              color: 'primary.main',
            },
          }}
        >
          Conta Recebimento
        </Button>

        <Box sx={{ ml: 'auto', alignSelf: 'end', display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button
            startIcon={isDownloading ? <CircularProgress size={20} /> : <FileDownload />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              border: `1px solid ${theme.palette.primary.main}`,
              color: theme.palette.primary.main,
              padding: '8px 18px',
            }}
            onClick={handleDownloadTransactions}
            disabled={isDownloading}
          >
            {isDownloading ? 'Baixando...' : 'Exportar'}
          </Button>

          <Button
            startIcon={<DescriptionIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              border: `1px solid ${theme.palette.primary.main}`,
              color: theme.palette.primary.main,
              padding: '8px 18px',
            }}
            onClick={() => setIsExportModalOpen(true)}
          >
            Gerar extrato
          </Button>

          <Button
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              border: `1px solid ${theme.palette.primary.main}`,
              color: theme.palette.primary.main,
              padding: '8px 18px',
            }}
            onClick={() => setIsIncomeReportModalOpen(true)}
          >
            Baixar informe
          </Button>
        </Box>
      </Box>

      <ExportModal open={isExportModalOpen} accountType={accountType} onClose={() => setIsExportModalOpen(false)} />

      <IncomeReportModal open={isIncomeReportModalOpen} onClose={() => setIsIncomeReportModalOpen(false)} accountType={accountType} />
    </>
  );
};
